import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    IGenericTypeAheadDropdownConfig,
    ITypeAheadDropdownAction
} from '../../interfaces/type-ahead-dropdown-config.interface';
import { HighlightTextPipe } from '../../pipes/highlight-text.pipe';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { CDK_IMPORTS } from '../../shared/standalone-imports';

@Component({
    selector: 'lib-options-menu',
    templateUrl: './options-menu.component.html',
    styleUrls: ['./options-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe, ...CDK_IMPORTS, HighlightTextPipe]
})
export class OptionsMenuComponent<T> implements OnChanges, AfterViewInit {
    @Input() source?: T[];
    @Input() menuOpen?: boolean;
    @Input() config?: IGenericTypeAheadDropdownConfig<T>;
    @Input() selectedItem?: T;
    @Input() selectedItems?: T[];
    @Input() text: string;
    @Input() noResultsText?: string;
    @Input() action?: ITypeAheadDropdownAction;
    @Input() focusList?: boolean;
    @Input() isItemDisabled?: boolean;
    @Input() menuId?: string;

    @Output() selectedItemChange: EventEmitter<T>;
    @Output() menuOpenChange: EventEmitter<boolean>;
    @Output() selectedItemsChange = new EventEmitter<T[]>();
    @Output() listKeyDown = new EventEmitter<{
        event: KeyboardEvent;
        item: T;
    }>();
    @Output() runActionEmitter = new EventEmitter<void>();

    @ViewChild('itemsList')
    itemsList!: ElementRef<HTMLUListElement | undefined>;

    focusedIndex: number;

    constructor() {
        this.text = '';
        this.selectedItem = undefined;
        this.selectedItemChange = new EventEmitter<T>();
        this.menuOpenChange = new EventEmitter<boolean>();
        this.focusedIndex = 0;
    }

    ngAfterViewInit(): void {
        this.itemsList = this.itemsList as ElementRef<HTMLUListElement | undefined>;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['focusList'] && this.focusList) {
            this.focusFirstItem();
        }
        if (changes['source'] && this.source?.length && this.menuOpen && this.focusList) {
            setTimeout(() => this.focusListItem(), 0);
        }
    }

    onSelectItem(item: T) {
        this.selectedItemChange.emit(item);
    }

    onListKeyDown(event: KeyboardEvent, item: T): void {
        if (this.source && this.menuOpen) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (this.focusedIndex !== this.source.length - 1) {
                    this.focusedIndex++;
                    this.focusListItem();
                }
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (this.focusedIndex !== 0) {
                    this.focusedIndex--;
                    this.focusListItem();
                }
            } else if (event.key === 'Enter') {
                event.preventDefault();

                this.onSelectItem(item);
            } else if (event.key === 'Tab') {
                this.itemsList?.nativeElement?.childNodes.forEach((item) => ((item as HTMLLIElement).tabIndex = -1));
                if (this.config?.clearSelection || this.action?.includeAction) return;
                this.menuOpenChange.emit(false);
            } else if (event.key === 'Escape') {
                this.menuOpenChange.emit(false);
            }
        }
        this.listKeyDown.emit({ event, item });
    }

    onClearSelectionClicked() {
        if (!this.config?.clearSelection || this.config?.multiple) return;
        this.selectedItemChange.emit();
    }

    runAction(callback?: () => Promise<void>) {
        if (!callback) return;
        this.runActionEmitter.emit();
        callback();
    }

    getTemplate(): TemplateRef<unknown> | null {
        if (!this.config?.itemTemplate) return null;
        return this.config.itemTemplate;
    }

    focusListItem(forcedIndex?: number): void {
        const listElement = this.itemsList?.nativeElement;
        if (!listElement) return;

        const totalItems = listElement.children.length;

        let index = forcedIndex ?? this.focusedIndex;

        if (index >= totalItems) {
            index = totalItems - 1;
        } else if (index < 0) {
            index = 0;
        }

        const activeListItem = listElement.children[index] as HTMLLIElement | undefined;
        if (!activeListItem) return;

        Array.from(listElement.children).forEach((item) => {
            const liItem = item as HTMLLIElement;
            if (liItem === activeListItem) {
                liItem.tabIndex = 0;
                liItem.focus();
                this.updateFocusedItem(liItem);
            } else {
                liItem.tabIndex = -1;
            }
        });

        this.focusedIndex = index;
    }

    private focusFirstItem(): void {
        if (this.itemsList?.nativeElement?.children[0]) {
            const firstItem = this.itemsList.nativeElement.children[0] as HTMLLIElement;
            firstItem.tabIndex = 0;
            firstItem.focus();
            this.focusedIndex = 0;
            this.updateFocusedItem(firstItem);
        }
    }

    private updateFocusedItem(focusedElement: HTMLElement): void {
        this.itemsList?.nativeElement?.querySelectorAll('li').forEach((item: Element) => {
            item.classList.remove('focused');
        });
        focusedElement.classList.add('focused');
    }
}
